import { Icon } from 'components/common/icons/Icon';
import React from 'react';
import { IconType } from 'types/IconType';
import { Monitor } from './Monitor';
import { getImage } from 'gatsby-plugin-image';
import { graphql, useStaticQuery } from 'gatsby';

type FeatureCircleProps = {
    title: string | JSX.Element;
    paragraph: string | JSX.Element;
};

export const FeatureCircle = ({ title, paragraph }: FeatureCircleProps) => {
    const { productTourPoster } = useStaticQuery(graphql`
        query {
            productTourPoster: file(
                relativePath: { eq: "pages/industry/insurance/product-tour-poster.jpg" }
            ) {
                childImageSharp {
                    gatsbyImageData(placeholder: BLURRED)
                }
            }
        }
    `);

    const getCycleBox = (
        title: string,
        paragraph: string,
        arrowDetails: { positionClassNames: string; colorClassNames: string; rotation: string },
        icon: IconType,
        className?: string
    ) => {
        const { colorClassNames, positionClassNames, rotation } = arrowDetails;
        return (
            <div className="tw-flex lg:tw-justify-center">
                <div
                    className={`${className} lg:tw-px-24 lg:tw-pt-48 lg:tw-pb-24 tw-relative tw-rounded-large lg:tw-drop-shadow-lg tw-flex tw-justify-center tw-items-start lg:tw-text-center lg:tw-gap-0 tw-gap-12 lg:tw-flex-row tw-flex-col 2xl:tw-w-[240px] lg:tw-bg-[#fff]`}
                >
                    <h3 className="lg:tw-absolute -tw-top-24 lg:tw-p-12 tw-p-8 lg:tw-rounded-large tw-rounded tw-text-neutral-inverse lg:tw-text-xl tw-flex tw-gap-8 tw-items-center tw-font-bold tw-pr-24 tw-tracking-wider tw-bg-interactive">
                        <Icon icon={icon} />
                        {title}
                    </h3>
                    <p>{paragraph}</p>
                    <div
                        className={`${colorClassNames} ${positionClassNames} 2xl:tw-flex tw-hidden tw-absolute tw-w-48 tw-h-48 tw-items-center tw-justify-center tw-bg-[#fff] tw-rounded-full tw-border-[5px]`}
                    >
                        <div
                            className={`${colorClassNames} tw-w-16 tw-h-16 tw-border-b-[5px] tw-border-r-[5px]`}
                            style={{
                                transform: `rotate(${rotation})`
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="container tw-@container">
            <div className="lg:tw-prose-xl tw-prose tw-mx-auto lg:tw-text-center">
                <h2 className="tw-text-primary">{title}</h2>
                <p>{paragraph}</p>
            </div>
            <div className="2xl:tw-mt-[200px] tw-mt-64 tw-relative 2xl:tw-pb-[260px]">
                <div className="tw-grid lg:tw-grid-cols-3 lg:tw-justify-center tw-items-center 2xl:tw-gap-[120px] lg:tw-gap-24 tw-gap-48 2xl:tw-px-[120px] tw-relative tw-z-[1] lg:tw-max-w-none tw-max-w-[65ch] tw-mx-auto">
                    {getCycleBox(
                        'COACH',
                        'Reinforce good behaviors and build better habits',
                        {
                            colorClassNames: 'tw-border-primary-dark',
                            positionClassNames: '-tw-right-24 -tw-bottom-24',
                            rotation: '0deg'
                        },
                        'coaching-filled'
                    )}
                    {getCycleBox(
                        'VISUALIZE',
                        'Instill accountability with actionable real-time data',
                        {
                            colorClassNames: 'tw-border-primary',
                            positionClassNames: '-tw-bottom-24',
                            rotation: '45deg'
                        },
                        'screen-filled',
                        '2xl:-tw-translate-y-64'
                    )}
                    {getCycleBox(
                        'REWARD',
                        'Recognize hard work with more than just quartely bonuses',
                        {
                            colorClassNames: 'tw-border-primary-dark',
                            positionClassNames: '-tw-left-24 -tw-bottom-24',
                            rotation: '90deg'
                        },
                        'rewards-filled'
                    )}
                </div>
                <div className="tw-flex 2xl:tw-justify-between lg:tw-justify-center 2xl:tw-max-w-none lg:tw-max-w-[800px] lg:tw-gap-24 tw-gap-48 lg:tw-mt-[130px] tw-mt-48 tw-relative tw-z-[1] lg:tw-flex-nowrap tw-flex-wrap tw-max-w-[65ch] tw-mx-auto">
                    {getCycleBox(
                        'COMPETE',
                        'Boost productivity on daily activities which lead to big wins',
                        {
                            colorClassNames: 'tw-border-primary',
                            positionClassNames: '-tw-right-24 -tw-bottom-24',
                            rotation: '-45deg'
                        },
                        'competition-filled',
                        '2xl:-tw-translate-x-[120px]'
                    )}
                    {getCycleBox(
                        'CELEBRATE',
                        'Broadcast wins within daily and ongoing efforts to improve morale',
                        {
                            colorClassNames: 'tw-border-primary',
                            positionClassNames: '-tw-left-24 -tw-bottom-24',
                            rotation: '145deg'
                        },
                        'celebration-filled',
                        '2xl:tw-translate-x-[120px]'
                    )}
                </div>
                <div className="2xl:tw-absolute tw-w-full tw-flex tw-justify-center 2xl:-tw-bottom-[200px] tw-z-[1] 2xl:tw-m-0 tw-my-48">
                    <Monitor
                        videoUrl="https://content.salesscreen.com/hermes-cms/salesscreen-product-tour.mp4"
                        videoPoster={getImage(productTourPoster).images.fallback.src}
                    />
                </div>
                <div
                    className="2xl:tw-inline-block tw-hidden tw-absolute tw-w-[1200px] tw-h-[1200px] tw-rounded-full -tw-top-48 tw-left-0 tw-z-0 tw-p-48"
                    style={{
                        background: 'linear-gradient(150deg, #1eafaa 0%, rgb(0, 0, 0) 60%)'
                    }}
                >
                    <div className="tw-bg-[#fff] tw-w-full tw-h-full tw-rounded-full" />
                </div>
            </div>
        </div>
    );
};
